import type { CampaignSpreadsheetViewType } from '../../context/CampaignContext';
import {
  getActiveProspectPrioritizationWorkflowId,
  startProspectPrioritizationExecute,
  streamProspectPrioritizationResult
} from '../../api/campaignPrioritization';
import type { ReconnectPrioritizationContext } from './spreadsheetTypes';

/**
 * Throw from `fetchRowIds` after user-facing UX (e.g. snackbar) so the panel start path aborts without a generic failure toast.
 * `runProspectPrioritizationFromPanel` catches this and returns silently.
 */
export class PrioritizationAbortNoProspectsError extends Error {
  readonly name = 'PrioritizationAbortNoProspectsError';
  constructor() {
    super('prioritization_aborted_no_prospects');
  }
}

/** Debounce between SSE RUNNING-driven highlight refreshes (GET …/prioritizing only; avoids hammering the API). */
const PRIORITIZING_HIGHLIGHTS_SYNC_DEBOUNCE_MS = 800;

export interface RunProspectPrioritizationContext {
  getHeaderState: () => {
    columnNamesByIndex: string[];
    columns: unknown[];
  } | null;
  prioritizingCellsRef: { current: Set<string> };
  setPrioritizingCellsCount: (n: number) => void;
  setPrioritizationSheetLock: (locked: boolean) => void;
  setActivePrioritizationWorkflowId: (id: string | null) => void;
  prioritizationStreamCancelRef: { current: (() => void) | null };
  showSnackbar: (
    message: string,
    severity: 'error' | 'warning' | 'info' | 'success'
  ) => void;
  requestSheetRepaint: () => void;
  completePrioritization: () => Promise<void>;
  /** Refetch GET …/prioritizing and repaint. Optionally merge columns/cells from GET /columns + /cells (heavy). */
  syncPrioritizingCellsForWorkflow: (
    workflowId: string,
    options?: { mergeSheetFromBackend?: boolean }
  ) => Promise<void>;
  /** Merge new prioritization columns/cells from backend into the sheet (same as apply on ref). */
  applyPrioritizationResultFromBackend: () => Promise<void>;
  /**
   * Ensure prioritization output columns exist on the Univer sheet + header state only
   * (no `createColumn` API). Backend may create the same columns separately.
   */
  ensurePrioritizationOutputColumnsOnSheet: (
    outputColumnNames: string[]
  ) => Promise<void>;
  /** No-op cell writes + repaint so in-progress highlights appear without resize. */
  flushPrioritizingHighlights: () => void;
}

/** Rebuild `row_id:colIndex` keys for output columns after backend or header state changes. */
function rebuildPrioritizingCellKeys(
  ctx: Pick<
    RunProspectPrioritizationContext,
    'getHeaderState' | 'prioritizingCellsRef' | 'setPrioritizingCellsCount'
  >,
  rowIds: number[],
  outputColumnNames: Set<string>
): void {
  const state = ctx.getHeaderState();
  ctx.prioritizingCellsRef.current.clear();
  if (!state?.columnNamesByIndex?.length || rowIds.length === 0) {
    ctx.setPrioritizingCellsCount(0);
    return;
  }
  for (const rowId of rowIds) {
    for (let c = 0; c < state.columnNamesByIndex.length; c++) {
      if (outputColumnNames.has(state.columnNamesByIndex[c])) {
        ctx.prioritizingCellsRef.current.add(`${rowId}:${c}`);
      }
    }
  }
  ctx.setPrioritizingCellsCount(ctx.prioritizingCellsRef.current.size);
}

function attachProspectPrioritizationStream(
  campaignId: string,
  workflowId: string,
  viewType: CampaignSpreadsheetViewType | undefined,
  options: {
    ctx: Pick<
      RunProspectPrioritizationContext,
      | 'prioritizationStreamCancelRef'
      | 'prioritizingCellsRef'
      | 'setPrioritizingCellsCount'
      | 'setActivePrioritizationWorkflowId'
      | 'setPrioritizationSheetLock'
      | 'showSnackbar'
      | 'requestSheetRepaint'
      | 'completePrioritization'
      | 'syncPrioritizingCellsForWorkflow'
    >;
    /** When false, only show success/error toasts if we saw RUNNING on this connection (reload hygiene). */
    userInitiated: boolean;
  }
): () => void {
  const { ctx, userInitiated } = options;
  let sawRunning = userInitiated;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const clearSyncDebounce = () => {
    if (debounceTimer != null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  };

  const scheduleSyncPrioritizing = () => {
    clearSyncDebounce();
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void ctx.syncPrioritizingCellsForWorkflow(workflowId, {
        mergeSheetFromBackend: false
      });
    }, PRIORITIZING_HIGHLIGHTS_SYNC_DEBOUNCE_MS);
  };

  ctx.prioritizationStreamCancelRef.current?.();
  const streamCancel = streamProspectPrioritizationResult(
    campaignId,
    workflowId,
    {
      onStatus: (status, error) => {
        if (status === 'RUNNING') {
          sawRunning = true;
          scheduleSyncPrioritizing();
        }
        if (status === 'FAILED' && error && sawRunning) {
          ctx.showSnackbar(error, 'error');
        }
        if (status === 'COMPLETED' && sawRunning) {
          ctx.showSnackbar('Prioritization completed successfully', 'success');
        }
      },
      onComplete: async () => {
        clearSyncDebounce();
        ctx.prioritizationStreamCancelRef.current = null;
        try {
          await ctx.completePrioritization();
        } finally {
          ctx.prioritizingCellsRef.current.clear();
          ctx.setPrioritizingCellsCount(0);
          ctx.setActivePrioritizationWorkflowId(null);
          ctx.setPrioritizationSheetLock(false);
          ctx.requestSheetRepaint();
        }
      },
      onError: (message) => {
        clearSyncDebounce();
        ctx.prioritizationStreamCancelRef.current = null;
        ctx.prioritizingCellsRef.current.clear();
        ctx.setPrioritizingCellsCount(0);
        ctx.setActivePrioritizationWorkflowId(null);
        ctx.setPrioritizationSheetLock(false);
        ctx.requestSheetRepaint();
        if (sawRunning) {
          ctx.showSnackbar(`Prioritization failed: ${message}`, 'error');
        }
      }
    },
    viewType
  );
  const cancel = () => {
    clearSyncDebounce();
    streamCancel();
  };
  ctx.prioritizationStreamCancelRef.current = cancel;
  // Initial merge after attach: pull cells (and any sheet-only schema) from GET /columns + /cells.
  // Debounced RUNNING syncs use mergeSheetFromBackend: false (highlights only).
  void ctx.syncPrioritizingCellsForWorkflow(workflowId, {
    mergeSheetFromBackend: true
  });
  return cancel;
}

function clearProspectPrioritizationReconnectState(
  ctx: Pick<
    ReconnectPrioritizationContext,
    | 'prioritizationStreamCancelRef'
    | 'prioritizingCellsRef'
    | 'setPrioritizingCellsCount'
    | 'setActivePrioritizationWorkflowId'
    | 'setPrioritizationSheetLock'
    | 'requestSheetRepaint'
  >
): void {
  ctx.prioritizationStreamCancelRef.current?.();
  ctx.prioritizationStreamCancelRef.current = null;
  ctx.prioritizingCellsRef.current.clear();
  ctx.setPrioritizingCellsCount(0);
  ctx.setActivePrioritizationWorkflowId(null);
  ctx.setPrioritizationSheetLock(false);
  ctx.requestSheetRepaint();
}

/** Panel-driven start: ensure output columns on sheet, POST execute, then SSE (initial sync merges cells). */
export async function runProspectPrioritizationFromPanel(
  campaignId: string,
  viewType: CampaignSpreadsheetViewType | undefined,
  fetchRowIds: () => Promise<number[]>,
  outputColumnNames: string[],
  ctx: RunProspectPrioritizationContext
): Promise<void> {
  try {
    const rowIds = await fetchRowIds();
    // Must stay before header/column/Side effects: zero rows ⇒ no POST, no sheet column ensure.
    if (rowIds.length === 0) {
      return;
    }
    const state = ctx.getHeaderState();
    if (!state?.columnNamesByIndex?.length) {
      return;
    }
    await ctx.ensurePrioritizationOutputColumnsOnSheet(outputColumnNames);

    ctx.setPrioritizationSheetLock(true);
    const outputNames = new Set(outputColumnNames);
    rebuildPrioritizingCellKeys(ctx, rowIds, outputNames);
    ctx.flushPrioritizingHighlights();

    const { workflow_id } = await startProspectPrioritizationExecute(
      campaignId,
      { row_ids: rowIds },
      viewType
    );
    rebuildPrioritizingCellKeys(ctx, rowIds, outputNames);
    ctx.setActivePrioritizationWorkflowId(workflow_id);
    ctx.setPrioritizationSheetLock(false);
    ctx.flushPrioritizingHighlights();

    attachProspectPrioritizationStream(campaignId, workflow_id, viewType, {
      ctx,
      userInitiated: true
    });
  } catch (err) {
    if (err instanceof PrioritizationAbortNoProspectsError) {
      return;
    }
    ctx.prioritizingCellsRef.current.clear();
    ctx.setPrioritizingCellsCount(0);
    ctx.setPrioritizationSheetLock(false);
    ctx.setActivePrioritizationWorkflowId(null);
    ctx.requestSheetRepaint();
    ctx.showSnackbar(
      `Failed to start prioritization: ${err instanceof Error ? err.message : String(err)}`,
      'error'
    );
  }
}

/**
 * On campaign mount: if backend reports an active prioritization workflow, reconnect to SSE.
 * Does not refill prioritizingCellsRef (sheet load used GET …/prioritizing for that).
 */
export function subscribeToActiveProspectPrioritizationWorkflow(
  campaignId: string | undefined,
  viewType: CampaignSpreadsheetViewType | undefined,
  ctx: ReconnectPrioritizationContext
): () => void {
  if (!campaignId) return () => {};
  let cancelled = false;
  let cancelStream: (() => void) | null = null;

  getActiveProspectPrioritizationWorkflowId(campaignId, viewType)
    .then((res) => {
      if (cancelled) return;
      if (!res?.workflow_id) {
        const hadStaleWorkflow = !!ctx.getActivePrioritizationWorkflowId?.();
        clearProspectPrioritizationReconnectState(ctx);
        if (hadStaleWorkflow) {
          void ctx.completePrioritization();
        }
        return;
      }
      ctx.setActivePrioritizationWorkflowId(res.workflow_id);
      cancelStream = attachProspectPrioritizationStream(
        campaignId,
        res.workflow_id,
        viewType,
        {
          ctx: {
            prioritizationStreamCancelRef: ctx.prioritizationStreamCancelRef,
            prioritizingCellsRef: ctx.prioritizingCellsRef,
            setPrioritizingCellsCount: ctx.setPrioritizingCellsCount,
            setActivePrioritizationWorkflowId:
              ctx.setActivePrioritizationWorkflowId,
            setPrioritizationSheetLock: ctx.setPrioritizationSheetLock,
            showSnackbar: ctx.showSnackbar,
            requestSheetRepaint: ctx.requestSheetRepaint,
            completePrioritization: ctx.completePrioritization,
            syncPrioritizingCellsForWorkflow:
              ctx.syncPrioritizingCellsForWorkflow
          },
          userInitiated: false
        }
      );
    })
    .catch(() => {
      if (!cancelled) ctx.setActivePrioritizationWorkflowId(null);
    });

  return () => {
    cancelled = true;
    cancelStream?.();
  };
}
